/**
 * Overlap Detection Utility
 *
 * Detects overlapping time entries within the same project using a hybrid precision approach
 * with "benefit of doubt" for form-created entries.
 *
 * Key concept:
 * - Form-created entries have seconds = 0 and milliseconds = 0 (users input HH:MM only)
 * - Timer-stopped entries have real seconds/milliseconds from when the timer was stopped
 * - When a user enters "12:00" in a form after a timer ended at "12:00:30", they likely
 *   meant their entry starts at or after 12:00, not exactly at 12:00:00.000
 *
 * Precision rules:
 * - If EITHER entry's boundary has zeroed seconds → use MINUTE precision ("benefit of doubt")
 * - If BOTH entries have real seconds → use SECOND precision (accurate timer data)
 */

/**
 * Minimal interface for time entries - only the fields needed for overlap detection
 * This allows the utility to work with both shared/types TimeEntry and services/timeTracking TimeEntry
 */
interface TimeEntryForOverlap {
  id: string;
  projectId?: string;
  startTime: string;
  endTime?: string;
}

/**
 * Check if a timestamp has zeroed seconds (likely form-created or legacy entry)
 * Form entries have seconds = 0 and milliseconds = 0 because users input HH:MM only
 */
export const hasZeroedSeconds = (date: Date): boolean => {
  return date.getSeconds() === 0 && date.getMilliseconds() === 0;
};

/**
 * Truncate timestamp to minute precision (floor to start of minute)
 * Used for "benefit of doubt" comparison when form entries are involved
 */
export const truncateToMinute = (date: Date): number => {
  return Math.floor(date.getTime() / 60000) * 60000;
};

/**
 * Check if two time ranges overlap using the "benefit of doubt" approach
 *
 * @param entryEnd - End time of the first entry
 * @param otherStart - Start time of the second entry
 * @returns true if the entries overlap, false otherwise
 */
export const checkOverlapWithBenefitOfDoubt = (
  entryEnd: Date,
  otherStart: Date
): boolean => {
  // Determine precision based on whether EITHER boundary has zeroed seconds:
  // - If EITHER has zeroed seconds → use minute precision ("benefit of doubt")
  // - If BOTH have real seconds → use second precision (accurate timer data)
  const eitherHasZeroedSeconds = hasZeroedSeconds(entryEnd) || hasZeroedSeconds(otherStart);

  if (eitherHasZeroedSeconds) {
    // Minute precision: give "benefit of doubt" to form entries
    // User entering "17:48" after a timer ending at 17:48:32 intended sequential
    const entryEndMinute = truncateToMinute(entryEnd);
    const otherStartMinute = truncateToMinute(otherStart);
    return otherStartMinute < entryEndMinute;
  } else {
    // Second precision: both entries have real timestamp data from timer
    return otherStart.getTime() < entryEnd.getTime();
  }
};

/**
 * Detect overlapping entries within the same project
 * Uses all-pairs comparison for thorough detection of overlaps
 *
 * @param entries - Array of time entries to check for overlaps
 * @returns Set of entry IDs that have overlaps
 */
export const detectOverlappingEntries = (entries: TimeEntryForOverlap[]): Set<string> => {
  const overlapping = new Set<string>();

  // Group entries by project
  const entriesByProject: Record<string, TimeEntryForOverlap[]> = {};
  entries.forEach(entry => {
    const projectId = entry.projectId || 'no-project';
    if (!entriesByProject[projectId]) {
      entriesByProject[projectId] = [];
    }
    entriesByProject[projectId].push(entry);
  });

  // Check for overlaps within each project (all pairs for thorough detection)
  Object.values(entriesByProject).forEach(projectEntries => {
    // Sort by start time
    const sorted = [...projectEntries].sort(
      (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );

    for (let i = 0; i < sorted.length; i++) {
      const entry = sorted[i];
      // entryEnd is what matters - we check if later entries start before this one ends
      const entryEnd = entry.endTime ? new Date(entry.endTime) : new Date();

      for (let j = i + 1; j < sorted.length; j++) {
        const other = sorted[j];
        // otherStart is what matters - since sorted by start, we check if this starts before prev ends
        const otherStart = new Date(other.startTime);

        if (checkOverlapWithBenefitOfDoubt(entryEnd, otherStart)) {
          overlapping.add(entry.id);
          overlapping.add(other.id);
        }
      }
    }
  });

  return overlapping;
};

/**
 * Detect overlapping entries using consecutive-only comparison
 * More efficient for sorted sequential entries (used in Reporting)
 *
 * Note: This may miss non-adjacent overlaps (e.g., entry A overlaps with C but not B)
 * Use detectOverlappingEntries for thorough detection
 *
 * @param entries - Array of time entries to check (should be pre-sorted by start time)
 * @returns Set of entry IDs that have overlaps
 */
export const detectOverlappingEntriesConsecutive = (
  sortedEntries: TimeEntryForOverlap[]
): Set<string> => {
  const overlapping = new Set<string>();

  for (let i = 1; i < sortedEntries.length; i++) {
    const prevEntry = sortedEntries[i - 1];
    const currEntry = sortedEntries[i];

    const prevEnd = prevEntry.endTime ? new Date(prevEntry.endTime) : new Date();
    const currStart = new Date(currEntry.startTime);

    if (checkOverlapWithBenefitOfDoubt(prevEnd, currStart)) {
      overlapping.add(prevEntry.id);
      overlapping.add(currEntry.id);
    }
  }

  return overlapping;
};
